import {
  faArrowDown,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef, useState, useEffect } from "react";
import { Col, Dropdown, DropdownButton, Row } from "react-bootstrap";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import CustomTooltip from "../../Elements/Tooltip";
import styles from "./PageByPage.module.css";
import StatPdfViewer from "./SessionComponents/StatPdfViewer";
import { viewsPercentage } from "../../../utis/helper";
import { useStats } from "./StatsContext";

const buildTimePerPage = (rawData, numPages) => {
  const pageMap = {};
  (rawData || []).forEach(item => { pageMap[item.page] = item; });
  return Array.from({ length: numPages }, (_, i) => {
    const page = i + 1;
    return pageMap[page] || { page, views: 0, secs_on_page: 0, total_min_on_page: "00:00:00" };
  });
};

const PageByPage = ({ }) => {
  const { selectedRecipientId, receiverStats, receiverStatsLoading, chartStats, chartStatsLoading } = useStats();

  const pdfFile = window.objInit.pdfFile;
  const doc = window.objInit.tl?.doc || window.objInit.doc;
  const defaultStats = window.objInit.tl?.stats || window.objInit.doc.stats;
  const elementsz = window?.objInit?.elements;

  // Priority: receiver stats > chart stats (threshold-filtered) > default
  const stats = (selectedRecipientId && receiverStats) ? receiverStats : (chartStats || defaultStats);

  const numPages = pdfFile?.num_pages || 0;

  const [timePerPage, setTimePerPage] = useState(() => buildTimePerPage(defaultStats?.time_per_page, numPages));
  const timePerPageRef = useRef(timePerPage);
  timePerPageRef.current = timePerPage;
  const [settings, setSettings] = useState({ page: 1, settings: "page_asc" });
  const disableBeforeChangeRef = useRef(false);
  const sliderRef = useRef(null);

  // Rebuild timePerPage when stats source changes (receiver selected/cleared)
  const prevRecipientRef = useRef(selectedRecipientId);
  useEffect(() => {
    if (prevRecipientRef.current !== selectedRecipientId) {
      prevRecipientRef.current = selectedRecipientId;
    }
    const data = buildTimePerPage(stats?.time_per_page, numPages);
    setTimePerPage(data);
    setSettings({ page: data[0]?.page || 1, settings: "page_asc" });
    if (sliderRef.current) {
      disableBeforeChangeRef.current = true;
      sliderRef.current.slickGoTo(0);
      setTimeout(() => { disableBeforeChangeRef.current = false; }, 600);
    }
  }, [selectedRecipientId, receiverStats, chartStats]);

  const sortingOptions = [
    { key: "time_desc", label: "Duration", icon: faArrowDown },
    { key: "time_asc", label: "Duration", icon: faArrowUp },
    { key: "seprator", label: "", icon: "" },
    {
      key: "page_asc",
      label: `Page 1 to ${pdfFile?.num_pages || "-"}`,
      icon: faArrowUp,
    },
    {
      key: "page_desc",
      label: `Page ${pdfFile?.num_pages || "-"} to 1`,
      icon: faArrowDown,
    },
  ];
  const selectedOption = sortingOptions.find(
    (option) => option.key === settings.settings
  );
  const getElementOfPage = (pageId) => {
    return Object.keys(elementsz)
      .map((elementKey) => elementsz[elementKey])
      .filter((element) => element.page == pageId);
  };

  const carouselSettings = {
    dots: false,
    infinite: pdfFile?.num_pages > 2 ? true : false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    beforeChange: (oldIndex, newIndex) => {
      if (disableBeforeChangeRef.current) return;
      setSettings((prev) => ({
        ...prev,
        page: timePerPageRef.current[newIndex]?.page || 1,
      }));
    },
    afterChange: () => {
      if (disableBeforeChangeRef.current) {
        disableBeforeChangeRef.current = false;
      }
    },
  };

  const handleUpdateSettingsPage = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setSettings((prev) => ({ ...prev, page: '' }));
      return;
    }
    const value = parseInt(raw, 10);
    if (isNaN(value) || value < 1) return;
    const maxPage = pdfFile?.num_pages || timePerPage.length;
    const clamped = Math.min(value, maxPage);
    setSettings((prev) => ({ ...prev, page: clamped }));
    disableBeforeChangeRef.current = true;
    const slideIndex = timePerPage.findIndex(item => item.page === clamped);
    sliderRef.current.slickGoTo(slideIndex >= 0 ? slideIndex : 0);
    setTimeout(() => { disableBeforeChangeRef.current = false; }, 600);
  };

  const convertToSeconds = (timeStr) => {
    const [hh, mm, ss] = timeStr
      .split(":")
      .map((t) => parseInt((t == "--" ? "00" : t) || 0, 10));
    return hh * 3600 + mm * 60 + ss;
  };

  const durationPercentage = (score, total) =>
    ((convertToSeconds(score) / convertToSeconds(total)) * 100).toFixed(2);

  const handleUpdateSort = (ekey) => {
    let sortedData;
    switch (ekey) {
      case "time_desc":
        sortedData = [...timePerPage].sort(
          (a, b) =>
            convertToSeconds(b.total_min_on_page) -
            convertToSeconds(a.total_min_on_page)
        );
        break;
      case "time_asc":
        sortedData = [...timePerPage].sort(
          (a, b) =>
            convertToSeconds(a.total_min_on_page) -
            convertToSeconds(b.total_min_on_page)
        );
        break;
      case "page_asc":
        sortedData = [...timePerPage].sort((a, b) => a.page - b.page);
        break;
      case "page_desc":
        sortedData = [...timePerPage].sort((a, b) => b.page - a.page);
        break;
      default:
        sortedData = [...timePerPage];
    }
    setTimePerPage((prev) => [...sortedData]);
    disableBeforeChangeRef.current = true;
    setSettings((prev) => ({
      ...prev,
      settings: ekey,
      page: sortedData[0]?.page || 1,
    }));
    sliderRef.current.slickGoTo(0);
    setTimeout(() => { disableBeforeChangeRef.current = false; }, 600);
  };

  if (receiverStatsLoading || chartStatsLoading) {
    return (
      <Row className="lower-glance justify-content-center">
        <Col md={12} className="text-center py-5">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading recipient stats...</p>
        </Col>
      </Row>
    );
  }

  return (
    <Row className={`lower-glance  justify-content-between`}>
      <Col md={5} >
      <div className="page-views-slider">
        <div className="d-flex align-items-center lower-content justify-content-around">
          <div className="d-flex align-items-center">
          <p>Page: </p>
          <input
            className="edit-value-inp ms-1 me-1"
            id="pageNumber"
            type="number"
            min={1}
            max={pdfFile?.num_pages}
            value={settings?.page}
            onChange={(e) => handleUpdateSettingsPage(e)}
          />
          <p>/{pdfFile?.num_pages || "-"}</p>
          </div>
          <div className="d-flex align-items-center">
          <p className="ms-3 me-2">Sort by:</p>
          <DropdownButton
            className={styles.directionDropDown}
            id="dropdown-basic-button"
            title={
              selectedOption ? (
                <span className="me-4">
                  <FontAwesomeIcon
                    icon={selectedOption.icon}
                    className="me-2"
                  />
                  {selectedOption.label}
                </span>
              ) : (
                "Select Sorting"
              )
            }
            onSelect={(eKey) => handleUpdateSort(eKey)}
            variant="secondary"
          >
            {sortingOptions?.map((option) =>
              option.key === "seprator" ? (
                <Dropdown.Divider key={option?.key} />
              ) : (
                <Dropdown.Item key={option.key} eventKey={option.key}>
                  <FontAwesomeIcon icon={option.icon} className="me-2" />
                  {option.label}
                </Dropdown.Item>
              )
            )}
          </DropdownButton>
          </div>
        </div>
        <div className={styles.carouselContainer}>
          <Slider {...carouselSettings} ref={sliderRef}>
            {timePerPage.map((data, index) => {
                const pageWasViewed = data?.views > 0;
                if (pageWasViewed) {
                  return (
                    <div key={index} className={styles.card}>
                      <div className={styles.section}>
                        <h5>Page views</h5>
                        <progress
                          value={viewsPercentage(data?.views, stats?.viewers)}
                          max="100"
                        ></progress>
                        <p>
                          This page is viewed in {data?.views} out of{" "}
                          {stats?.viewers} visits.
                        </p>
                      </div>
                      {stats?.most_viewed_pages?.pages?.includes(data?.page) ? (
                        <CustomTooltip title={<p className="p-1">Most Visited Page</p>}>
                          <img
                            src="/images/green-medal.svg"
                            alt="Most Viewed"
                            className={styles.badgeImage}
                          />
                        </CustomTooltip>
                      ) : (
                        ""
                      )}
                      {data.page_exists != null ? (
                        <div className={styles.section}>
                          <h5>Exit page</h5>
                          <progress
                            value={viewsPercentage(data?.views, stats?.viewers)}
                            max="100"
                          ></progress>
                          <p>
                            {data.page_exists} out of {stats?.viewers} people
                            exited on this page
                          </p>
                        </div>
                      ) : (
                        ""
                      )}
                      <div className={styles.section}>
                        <h5>Duration</h5>
                        <progress
                          value={durationPercentage(
                            data.total_min_on_page,
                            stats?.total_min_viewed
                          )}
                          max="100"
                        ></progress>
                        <p>
                          {data.total_min_on_page}/--:{stats?.total_min_viewed}
                        </p>
                      </div>
                      <div className={styles.interactive}>
                        <h6>Interactive Elements</h6>
                        {getElementOfPage(settings?.page)?.length > 0 ? (
                          getElementOfPage(settings?.page)?.map((elem) => (
                            <p
                              key={elem.id}
                              className="text-link-success d-flex justify-content-between border-bottom"
                            >
                              <span className="text-link-success cursor-pointer">
                                Element {elem.id}
                              </span>
                              <CustomTooltip title="Total clicks on this element across all sessions">
                                <a href="#/views" className="text-link-success cursor-pointer">
                                  {stats?.element_clicks?.[elem.id] ?? 0}
                                </a>
                              </CustomTooltip>
                            </p>
                          ))
                        ) : (
                          <p className="text-muted">No interactive elements on this page</p>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  return <div className="page-not-view">This page was not viewed</div>;
                }
            })}
          </Slider>
        </div>
        </div>
      </Col>
      <Col md={6}>
      <div className="main-coloums slider-stats pt-1 ">
        <div className="w-100">
          <p className="mb-2 text-center">Page preview</p>
          <StatPdfViewer
            pdfFile={pdfFile}
            doc={doc}
            selectedPage={settings?.page}
            sessionTabActive={false}
          />
        </div>
        </div>
      </Col>
    </Row>
  );
};

export default PageByPage;
